import React from "react";
import PropTypes from "prop-types";
import {buildStyles} from "../buildStyles";
import {useTheme} from "../../theme/ThemeProvider";
import FlexContainer from "./FlexContainer";
import Container from "./Container";

const useStyles = (theme, spacing) =>
  buildStyles({
    gridContainer: {
      display: "flex",
      width: "100%",
    },
    columnContainer: {
      display: "flex",
      flexDirection: "column",
    },
    gridItem: {
      marginBottom: theme.spacing(spacing),
      boxSizing: "border-box",
    },
  });

/**
 * DynamicGridLayout component that handles responsive grid layouts
 *
 * @param {Object} props
 * @param {node} props.children - The grid items to display
 * @param {number} props.spacing - The spacing between grid items in pixels
 * @param {Object} props.breakpoints - An object where keys are pixel widths and values are column counts
 *                                    Example: { 0: 1, 768: 2, 1200: 3 } means:
 *                                    - 1 column from 0-767px
 *                                    - 2 columns from 768-1199px
 *                                    - 3 columns from 1200px+
 */
const DynamicGridLayout = ({
  children,
  spacing = 2,
  breakpoints = {0: 1, 1000: 2}, // I added defaults, because I have a tendancy to forget things and wonder why it crashes...
  ...rest
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme, spacing);

  const getColumnCount = React.useCallback(() => {
    const width = window.innerWidth;
    const sortedBreakpoints = Object.entries(breakpoints).sort(
      ([a], [b]) => Number(b) - Number(a),
    );

    for (const [breakpoint, columns] of sortedBreakpoints) {
      if (width >= Number(breakpoint)) {
        return columns;
      }
    }

    return Object.entries(breakpoints).sort(
      ([a], [b]) => Number(a) - Number(b),
    )[0][1];
  }, [breakpoints]);

  const [columnCount, setColumnCount] = React.useState(() => {
    if (typeof window !== "undefined") {
      return getColumnCount();
    }
    return Object.entries(breakpoints).sort(
      ([a], [b]) => Number(a) - Number(b),
    )[0][1];
  });

  React.useEffect(() => {
    const handleResize = () => {
      const newColumnCount = getColumnCount();
      setColumnCount(newColumnCount);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [getColumnCount]);

  const childrenArray = React.Children.toArray(children).filter(Boolean);
  const columns = Array.from({length: columnCount}, () => []);

  childrenArray.forEach((child, index) => {
    const columnIndex = index % columnCount;
    columns[columnIndex].push(child);
  });

  return (
    <FlexContainer className={classes.gridContainer} {...rest}>
      {columns.map((columnItems, columnIndex) => {
        const isLastColumn = columnIndex === columnCount - 1;
        return (
          <Container
            key={`column-${columnIndex}`}
            className={classes.columnContainer}
            sx={{
              flex: 1,
              minWidth: 0,
              marginRight: isLastColumn ? 0 : theme.spacing(spacing),
            }}
          >
            {columnItems.map((child, itemIndex) => {
              const originalIndex = childrenArray.findIndex((c) => c === child);
              return (
                <Container
                  key={`grid-item-${originalIndex}`}
                  className={classes.gridItem}
                >
                  {child}
                </Container>
              );
            })}
          </Container>
        );
      })}
    </FlexContainer>
  );
};

DynamicGridLayout.propTypes = {
  children: PropTypes.node,
  spacing: PropTypes.number,
  breakpoints: PropTypes.object,
  className: PropTypes.string,
};

export default DynamicGridLayout;
